import { createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";
import { cloudChiprApi } from "../../../../services/cloudchipr.api";
import {
  setResourceExplorerFilterTree,
  setResourceExplorerGrouping,
} from "../../resourceExplorerSlice";
import { setResourceExplorerPathThunk } from "@/store/resource-explorer/thunks/widgets/path/setResourceExplorerPathThunk";
import { resourceExplorerGroupingPossibleGroupingsSelector } from "@/store/resource-explorer/selectors/possible-groupings/resourceExplorerGroupingPossibleGroupingsSelector";
import { RootState } from "@/store/store";
import { FilterTreeNodeWithId } from "@/components/pages/common/filters-tree/utils/types/common";
import { ResourceExplorerPath } from "@/store/resource-explorer/utils/types/billingExplorer";

interface SetResourceExplorerDimensionDataFromPathThunkArgs {
  dimensionId?: string;
  categoryId?: string;
  enableCategoryGroupingFilterV2: boolean;
}

export const setResourceExplorerDimensionDataFromPathThunk = createAsyncThunk(
  "resourceExplorer/setResourceExplorerDimensionFilterFromPath",
  async (
    {
      dimensionId,
      categoryId,
      enableCategoryGroupingFilterV2,
    }: SetResourceExplorerDimensionDataFromPathThunkArgs,
    { dispatch, getState },
  ) => {
    if (!dimensionId) {
      return;
    }

    let categoryIds = categoryId ? [categoryId] : [];

    const { getUsersMeOrganisationsCurrentDimensionsByDimensionId } =
      cloudChiprApi.endpoints;

    const response = await dispatch(
      getUsersMeOrganisationsCurrentDimensionsByDimensionId.initiate({
        dimensionId,
      }),
    ).unwrap();

    if (!response) {
      return;
    }

    const dimensionName = response?.name;
    if (!categoryId) {
      categoryIds = response.categories.map(({ id }) => id);
    }

    const selectedCategoryName =
      response?.categories?.find((item) => item.id === categoryId)?.name ?? "";

    const categoryNames = categoryId
      ? [selectedCategoryName]
      : (response?.categories?.map((item) => {
          return enableCategoryGroupingFilterV2 ? item.name : item.id;
        }) ?? []);

    const filterValue = {
      key: enableCategoryGroupingFilterV2 ? dimensionName : dimensionId,
      value: enableCategoryGroupingFilterV2 ? categoryNames : categoryIds,
    };

    const dimensionsFilterTreeFilters: FilterTreeNodeWithId = {
      node_type: "group",
      operator: "and",
      items: [
        {
          node_type: "group",
          operator: "and",
          items: [
            {
              id: uuid(),
              node_type: "item",
              type: enableCategoryGroupingFilterV2
                ? "category"
                : "dimension_id",
              filter_provider: "dimensions",
              operator: "in",
              value: filterValue,
            },
          ],
        },
      ],
    };
    const state = getState() as RootState;
    const possibleGroupings =
      resourceExplorerGroupingPossibleGroupingsSelector(state);

    const groupedBy = enableCategoryGroupingFilterV2
      ? "category_v2"
      : "category";
    const pathItem: ResourceExplorerPath = {
      groupedBy,
      groupValues: enableCategoryGroupingFilterV2
        ? [dimensionName]
        : [dimensionId],
      groupingOptions: possibleGroupings?.[groupedBy] ?? [],
      name: dimensionName,
    };

    dispatch(setResourceExplorerFilterTree(dimensionsFilterTreeFilters));
    dispatch(setResourceExplorerPathThunk([pathItem]));
    dispatch(
      setResourceExplorerGrouping({
        grouping: groupedBy,
        groupValues: enableCategoryGroupingFilterV2
          ? [dimensionName]
          : [dimensionId],
      }),
    );
    return dimensionsFilterTreeFilters;
  },
);
